import React, { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import IconProvider from '../ui/IconProvider';
import { getCachedDialogs } from '@/lib/stores/dialogStore';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { getStatusColor } from '@/lib/utils/statusColors';
import { useNavigate } from 'react-router-dom';
import CardBase from '../cards/CardBase';

interface DialogNodeData {
  itemId: string;
  itemType: 'dialog';
  label: string;
  expanded?: boolean;
  onToggleExpand?: () => void;
  [key: string]: any;
}

export const DialogNode = memo(({ data, selected }: NodeProps) => {
  const { selectedGame } = useSimplifiedUserData();
  const navigate = useNavigate();
  const nodeData = data as DialogNodeData;
  
  // Get the actual dialog data
  const dialogs = selectedGame?.id ? getCachedDialogs(selectedGame.id) : [];
  const dialog = dialogs.find(dlg => dlg.id === nodeData.itemId);
  
  // Get status color
  const statusColor = dialog?.status ? getStatusColor(dialog.status, 'dialogs', selectedGame?.id) : undefined;
  
  // Count sections
  const sectionCount = dialog?.data?.sections?.length || 0;
  
  const handleOpenInTab = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (dialog?.id) {
      navigate(`/dialogs/${dialog.id}`);
    }
  };
  
  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    nodeData.onToggleExpand?.();
  };
  
  return (
    <CardBase
    shadowType="black"
    shadowColor={"#000000"}
    backgroundColor={"#ffffff"}
    border={true}
    borderColor={"#000000"}
    className="min-w-[200px] max-w-[300px]"
    >
      {/* Connection Handles */}
      <Handle 
        type="target" 
        position={Position.Top}
        id="top"
        className="w-3 h-3 !bg-primary"
      />
      <Handle 
        type="source" 
        position={Position.Bottom}
        id="bottom"
        className="w-3 h-3 !bg-primary"
      />
      <Handle 
        type="target" 
        position={Position.Left}
        id="left"
        className="w-3 h-3 !bg-primary"
      />
      <Handle 
        type="source" 
        position={Position.Right}
        id="right"
        className="w-3 h-3 !bg-primary"
      />
      
      {/* Header with icon and actions */}
      <div 
        className="flex items-center gap-2 px-3 py-2 rounded-t-md bg-gradient-to-r from-purple-500 to-pink-500"
      >
        <div className="flex items-center justify-center w-6 h-6 rounded bg-white bg-opacity-20">
          <IconProvider iconstring="fa-comments" size={14} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {nodeData.label}
          </div>
        </div>
        <button
          onClick={handleToggleExpand}
          className="w-6 h-6 flex items-center justify-center rounded hover:bg-white hover:bg-opacity-20 transition-colors"
          title="Expand"
        >
          <IconProvider iconstring="fa-expand" size={12} className="text-white" />
        </button>
        <button
          onClick={handleOpenInTab}
          className="w-6 h-6 flex items-center justify-center rounded hover:bg-white hover:bg-opacity-20 transition-colors"
          title="Open in tab"
        >
          <IconProvider iconstring="fa-external-link-alt" size={12} className="text-white" />
        </button>
      </div>
      
      {/* Body with details */}
      <div className="px-3 py-2 space-y-1">
        {dialog?.dialog_key && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <IconProvider iconstring="fa-key" size={10} />
            <span className="truncate font-mono">{dialog.dialog_key}</span>
          </div>
        )}
        
        {sectionCount > 0 && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <IconProvider iconstring="fa-list" size={10} />
            <span className="truncate">{sectionCount} section(s)</span>
          </div>
        )}
        
        {dialog?.tags && dialog.tags.length > 0 && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <IconProvider iconstring="fa-tags" size={10} />
            <span className="truncate">{dialog.tags.length} tag(s)</span>
          </div>
        )}
        
        {dialog?.status && (
          <div className="flex items-center gap-1 text-xs">
            <div 
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: statusColor }}
            />
            <span className="truncate" style={{ color: statusColor }}>
              {dialog.status}
            </span>
          </div>
        )}
        
        {dialog?.description && (
          <div className="text-xs text-muted-foreground line-clamp-2 mt-1">
            {dialog.description}
          </div>
        )}
      </div>
    </CardBase>
  );
});

DialogNode.displayName = 'DialogNode';

